export interface GlossaryEntry {
  term: string;
  aliases?: string[];
  definition: string;
}

export const MARKING_METRICS_GLOSSARY: GlossaryEntry[] = [
  {
    term: 'Case',
    aliases: ['cases', 'ground-truth case', 'fragment'],
    definition:
      'The unit this page counts — not a label. A case is the resolved human outcome for one criterion on one fragment of one transcription (a sentence, a paragraph, or the whole transcription, matching granularity). It can come from unanimous labels, from implicit no-attempt on fragments a labeller did not select, or from a disagreement review that overrides labels. Conflicting labels do not create a case. Sentence-level criteria produce many cases per transcription; essay-level criteria produce one. A compared case is a ground-truth case that also has a current-policy AI mark, so it can enter the metrics.',
  },
  {
    term: 'Class',
    aliases: ['outcome class', 'positive', 'negative', 'both', 'no attempt'],
    definition:
      'One of the four possible marks a human or the AI can give: positive (the criterion is met correctly), negative (it is not, or is used incorrectly), both (correct and incorrect uses in the same fragment), or no attempt (the student did not try it). Precision, recall, and F1 are calculated one class at a time.',
  },
  {
    term: 'Compared case',
    aliases: ['compared cases', 'AI marked'],
    definition:
      'A ground-truth case that also has a current-policy AI mark. Only compared cases enter exact agreement, precision, recall, and F1. Evaluation coverage shows this as GT / AI marked: how many ground-truth cases exist versus how many of them the current eval run has marked.',
  },
  {
    term: 'Criterion',
    aliases: ['criteria'],
    definition:
      'One marking rubric item — a single thing student writing is judged on. Each row on this page is one criterion. Its granularity decides whether a case is a sentence, a paragraph, or a whole transcription.',
  },
  {
    term: 'Disagreement review',
    aliases: ['reviewed output', 'unresolved disagreement'],
    definition:
      'A review that compares human labels with AI marks for the same criterion and transcription, then records the correct outcome. A resolved disagreement review becomes the authoritative ground truth for that case, overriding label consensus. A passing, not-live criterion only enters Ready for expert review after those disagreements have been reviewed, or when exact agreement is 100%. Outstanding (unresolved) disagreements stay in Needs disagreement review.',
  },
  {
    term: 'Eval',
    aliases: ['eval run', 'eval marks', 'evaluation coverage'],
    definition:
      'An evaluation marking run whose purpose is measuring the AI, not producing live marks for teachers. Evaluation coverage splits ground-truth cases into current, missing, outdated, invalid, and unresolved-identity, so you can see which cases still need a current eval mark before they count.',
  },
  {
    term: 'Evidence',
    aliases: ['sufficient', 'insufficient evidence'],
    definition:
      'The gate on whether performance numbers can be trusted. Evidence is sufficient only when there are enough compared cases and they come from enough distinct transcriptions. Until then the scores are shown but greyed out and readiness is Insufficient evidence.',
  },
  {
    term: 'Exact agreement',
    aliases: ['agreement'],
    definition:
      'The share of compared cases where the AI chose the same class as the human. Easy to read, but it can hide a weak rare class if most cases are the common one (usually negative).',
  },
  {
    term: 'F1',
    aliases: ['f-score', 'harmonic mean'],
    definition:
      'A single score for one class that combines precision and recall. It is the harmonic mean, so a class only scores well if both precision and recall are good. An AI that is perfect on a tiny slice and misses the rest is punished more than a plain average would suggest.',
  },
  {
    term: 'Granularity',
    aliases: ['sentence', 'paragraph', 'essay'],
    definition:
      'The size of fragment a criterion is labelled and marked against: sentence, paragraph, or essay (the whole transcription). It sets how many cases one transcription can produce, which is why evidence thresholds are configured per granularity.',
  },
  {
    term: 'Ground truth',
    aliases: ['GT', 'human mark', 'human baseline'],
    definition:
      'The established human baseline for a case. It comes from a resolved disagreement review when one exists, otherwise from unanimous scorable labels on eval-eligible policy versions. Conflicting labels do not create a ground-truth case.',
  },
  {
    term: 'Label',
    aliases: ['labels', 'human label'],
    definition:
      "One labeller's stored assessment of a criterion on one fragment. Several labels can agree into a single case; an implicit no-attempt can become a case without an explicit label row; a disagreement review can replace labels entirely. This page counts cases, not label rows.",
  },
  {
    term: 'Live',
    aliases: ['in production', 'active'],
    definition:
      'Whether the criterion is currently active and markable in production. Readiness combines this with evidence and performance: live criteria that miss the thresholds need attention first.',
  },
  {
    term: 'Macro F1',
    aliases: ['macro precision', 'macro recall', 'macro average'],
    definition:
      'The equal-weight average of F1 across the four classes, regardless of how common each class is. A rare class such as both counts as a full quarter of the score, so it cannot hide behind the common ones. A class only joins this average once it has enough ground-truth cases (the min-class-support setting).',
  },
  {
    term: 'Precision',
    aliases: ['over-marking', 'false positive'],
    definition:
      'Of everything the AI marked as a given class, how much really was that class. Low precision means over-marking: credit given that a teacher would not give.',
  },
  {
    term: 'Readiness',
    aliases: [
      'meets standard',
      'ready for review',
      'needs attention',
      'needs improvement',
      'insufficient evidence',
    ],
    definition:
      'The single verdict per criterion, from three checks: enough evidence, performance at or above the saved exact-agreement and Macro F1 thresholds, and whether the criterion is live. The queues on the left group criteria by that verdict and by remaining work, so a passing criterion with outstanding disagreements is not treated as ready to go live.',
  },
  {
    term: 'Recall',
    aliases: ['under-marking', 'false negative'],
    definition:
      'Of everything that really was a given class, how much the AI found. Low recall means under-marking: credit withheld that a teacher would give.',
  },
  {
    term: 'Support',
    aliases: ['min class support', 'class support'],
    definition:
      'How many ground-truth cases a class has. A class with only one or two cases would swing Macro F1 on a single decision, so it only joins the macro average once it reaches the minimum support setting. Smaller classes are still shown in the details drawer, marked as not counted.',
  },
  {
    term: 'Transcription',
    aliases: ['essay', 'piece of writing'],
    definition:
      'One piece of student writing, already converted into text. Distinct transcriptions are the second evidence gate: enough cases is not enough if they all come from the same unusual essay.',
  },
  {
    term: 'Unjudged',
    aliases: ['eval eligibility', 'policy version'],
    definition:
      'A policy version that still has labels but no admin decision on whether those labels are safe to use as eval ground truth. Unjudged versions can exclude labels from the ground-truth set until someone marks them equivalent or different.',
  },
];

const haystack = (entry: GlossaryEntry): string =>
  [entry.term, ...(entry.aliases ?? []), entry.definition].join('\n').toLowerCase();

export const matchesGlossaryQuery = (entry: GlossaryEntry, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return haystack(entry).includes(needle);
};

const matchRank = (entry: GlossaryEntry, needle: string): number => {
  const term = entry.term.toLowerCase();
  if (term === needle) return 0;
  if (term.startsWith(needle)) return 1;
  if (term.includes(needle)) return 2;
  if ((entry.aliases ?? []).some(alias => alias.toLowerCase().includes(needle))) return 3;
  return 4;
};

export const filterGlossary = (entries: GlossaryEntry[], query: string): GlossaryEntry[] => {
  const needle = query.trim().toLowerCase();
  const matches = entries.filter(entry => matchesGlossaryQuery(entry, query));
  if (!needle) return matches;
  return [...matches].sort(
    (left, right) =>
      matchRank(left, needle) - matchRank(right, needle) || left.term.localeCompare(right.term)
  );
};
